"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSessionStorage,
  shouldShowDelayForm,
  setSessionStorage,
} from "@/sessionStorage";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import type { ILeadDropPopup } from "@/store/slices/university";
import { useResolvedLeadForm } from "@/components/organisms/leadForm/useResolvedLeadForm";
import { readPopupTriggers } from "@/types/leadForm";

/**
 * The two triggers that open the lead popup without anyone clicking a CTA: a delay timer
 * and exit intent.
 *
 * ─── One hook, two sources of settings ──────────────────────────────────────────────
 * There are two kinds of popup on a page and only ever ONE of them renders — `OpenLeadDrop`
 * hands the surface to a CMS-authored custom form when the page resolves one, and to the
 * university's legacy design variant otherwise. So the settings this hook obeys have to
 * follow the same fork:
 *
 *   a custom popup applies  →  ITS `trigger_config` (per form, travels with the form)
 *   otherwise               →  the university's `lead_drop_popup` row (per university)
 *
 * Reading both would auto-open a custom form the author never ticked a box for, on an
 * interval typed years ago for a different design. Resolving them here, rather than running
 * a second hook beside this one, is what makes that unrepresentable: two hooks racing on
 * `handleOpenLeaddrop` would open the dialog twice and fire two `popup_open` events.
 *
 * ─── Units differ, deliberately ─────────────────────────────────────────────────────
 * The legacy columns are milliseconds (`popup_display_interval_1..3`); a custom form's
 * intervals are SECONDS, because a box labelled "(ms)" is a standing invitation to type 30
 * and get an instant popup. Both are converted to ms exactly once, here.
 */
export const useDelayForm = () => {
  const isMobile = useIsMobile(800);
  const dispatch = useDispatch();

  const { isDelayFormOpen, formData, isOpen } = useSelector(
    (state: RootState) => state.leaddrop,
  );

  const isAppPromotionalBannerOpen = useSelector(
    (state: RootState) => state.globalslice.isPromotionalBannerOpen,
  );

  const floatingIconsVisibility = useSelector(
    (state: RootState) => state.globalslice.floatingIconsVisibility,
  );

  const { isPromotionalBannerEnabled } = floatingIconsVisibility;

  const isPromotionalBannerOpen = isAppPromotionalBannerOpen && isPromotionalBannerEnabled;

  /**
   * The CMS-authored popup for this page, if one applies — the same resolution
   * `OpenLeadDrop` uses to decide what to render, so the settings can never belong to a
   * popup other than the one on screen.
   */
  const customPopup = useResolvedLeadForm("popup");
  const customTriggerConfig = customPopup?.trigger_config ?? null;

  const {
    popup_display_interval_1,
    popup_display_interval_2,
    popup_display_interval_3,
    enable_delay_form,
    enable_exit_form,
  } = (formData ?? {}) as ILeadDropPopup;

  const interval1Ms = popup_display_interval_1;
  const interval2Ms = popup_display_interval_2;
  const interval3Ms = popup_display_interval_3;

  /**
   * The delays as a comma-joined string, not an array.
   *
   * A fresh array every render is a fresh dependency every render: the arming effect below
   * would clear its own pending timer and restart the countdown on each one, so a 30-second
   * delay would never elapse. The string is a stable primitive; the array is derived from it.
   */
  const { delayEnabled, exitEnabled, intervalsCsv } = useMemo(() => {
    if (customPopup) {
      const triggers = readPopupTriggers(customTriggerConfig);
      const ms = triggers.auto_open_intervals
        .map((seconds) => Math.round(Number(seconds) * 1000))
        .filter((n) => Number.isFinite(n) && n > 0);
      return {
        // A ticked box with no delay is refused at save, so this should not occur — but it
        // is the one combination that produces a timer with nothing to wait for.
        delayEnabled: triggers.auto_open_enabled && ms.length > 0,
        exitEnabled: triggers.exit_intent_enabled,
        intervalsCsv: ms.join(","),
      };
    }

    /**
     * Legacy: all THREE intervals must be set for any of them to run.
     *
     * That is a trap — an author who fills only the first gets nothing, with no error —
     * but it is the behaviour every live university is configured against today, so it is
     * preserved exactly. Custom forms deliberately do not inherit it: there, one delay
     * means one auto-open.
     */
    const legacy = [interval1Ms, interval2Ms, interval3Ms].map(Number);
    const allSet = legacy.every((n) => Number.isFinite(n) && n > 0);
    return {
      delayEnabled: Boolean(enable_delay_form) && allSet,
      exitEnabled: Boolean(enable_exit_form),
      intervalsCsv: allSet ? legacy.join(",") : "",
    };
  }, [
    customPopup,
    customTriggerConfig,
    interval1Ms,
    interval2Ms,
    interval3Ms,
    enable_delay_form,
    enable_exit_form,
  ]);

  const intervals = useMemo(
    () => (intervalsCsv ? intervalsCsv.split(",").map(Number) : []),
    [intervalsCsv],
  );

  //   const { isInputInteracted } = useSelector(
  //     (state: RootState) => state.leaddropForm
  //   );

  /** How many auto-opens have fired. Indexes into `intervals` for the next wait. */
  const callCountRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleShowExitForm = useCallback(() => {
    if ((getSessionStorage("show-on-exit") || 0) !== "2") {
      dispatch(
        handleOpenLeaddrop({
          isExitFormOpen: true,
        }),
      );
    }
  }, [dispatch, isOpen]);

  const callWithDelay = useCallback(
    (delay: number) => {
      timerRef.current = setTimeout(() => {
        // Don't dispatch if promotional banner is open
        if (isPromotionalBannerOpen) {
          timerRef.current = null;
          return;
        }

        if (shouldShowDelayForm()) {
          dispatch(
            handleOpenLeaddrop({
              isDelayFormOpen: true,
            }),
          );
        }

        callCountRef.current = callCountRef.current + 1;

        // Out of scheduled delays — mark the session done so a later page does not
        // restart the sequence. Only show-on-delay, never show-on-exit: exit intent is
        // counted separately and may still fire twice.
        if (callCountRef.current >= intervals.length) {
          setSessionStorage("show-on-delay", "1");
        }
        timerRef.current = null;
      }, delay);
    },
    [isPromotionalBannerOpen, dispatch, intervals],
  );

  //   useEffect(() => {
  //     if (!isInputInteracted && previousDelayRef.current === true) {
  //       callWithDelay(delaySecondsRef.current);
  //       return;
  //     }

  //     if (isInputInteracted) {
  //       if (timerRef.current) {
  //         previousDelayRef.current = true;
  //         clearTimeout(timerRef.current);
  //       }
  //     }
  //   }, [isInputInteracted]);

  /**
   * Exit intent — the mouse leaving the viewport on desktop, and passing the halfway
   * scroll mark on mobile, where there is no cursor to lose.
   */
  useEffect(() => {
    let triggerCount = 0;
    let hasPassedHalfOnce = false;

    /**
     * The mobile path has never checked the enable flag, unlike the desktop path below.
     *
     * Left that way for the LEGACY popup: every university with `enable_exit_form` off is
     * nonetheless getting this on mobile today, and switching it off here would quietly
     * take live leads away from them — a fix worth making deliberately, not as a side
     * effect of this change. A custom form's checkbox is new, so it governs both paths and
     * means what it says.
     */
    const exitOnScrollAllowed = customPopup ? exitEnabled : true;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const halfwayPoint = (documentHeight - windowHeight) / 2;

      if (scrollY >= halfwayPoint) {
        if (!hasPassedHalfOnce) {
          hasPassedHalfOnce = true;
          if (triggerCount < 2 && exitOnScrollAllowed) {
            handleShowExitForm();
            triggerCount++;
          }
        }
      } else {
        hasPassedHalfOnce = false;
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      // Check if mouse is leaving the viewport
      // relatedTarget is null when mouse leaves the document entirely

      if (!e.relatedTarget) {
        if (exitEnabled) {
          handleShowExitForm();
        }
      }
    };

    if (isMobile) {
      window.addEventListener("scroll", handleScroll);
    } else {
      // Use mouseout on document.body to detect when mouse leaves the viewport
      document.body.addEventListener("mouseout", handleMouseOut);
    }

    return () => {
      if (isMobile) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        document.body.removeEventListener("mouseout", handleMouseOut);
      }
    };
  }, [isMobile, exitEnabled, customPopup, handleShowExitForm, isPromotionalBannerOpen]);

  /**
   * The delay timer.
   *
   * Armed whenever there is a delay left to wait and nothing in the way, and disarmed while
   * the promotional banner is up — two dialogs opening over each other is worse than a
   * missed trigger, and the banner is the one that appeared first.
   */
  useEffect(() => {
    if (isPromotionalBannerOpen) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (
      !delayEnabled ||
      isDelayFormOpen ||
      timerRef.current ||
      callCountRef.current >= intervals.length
    ) {
      return;
    }

    // The first wait is measured from the page settling; each later one from the previous
    // close, which is why the index tracks opens rather than elapsed time.
    callWithDelay(intervals[callCountRef.current]);

    return () => {
      if (timerRef.current && !isPromotionalBannerOpen) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    isPromotionalBannerOpen,
    isDelayFormOpen,
    delayEnabled,
    intervals,
    callWithDelay,
  ]);
};
